import * as React from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { Routes, Route, Navigate, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as hardwareSelectors } from 'services/hardware';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { actions as willCallActions } from 'services/willcall';
import { actions as cartActions, selectors as cartSelectors } from 'services/cart';
import { selectors as kioskSelectors, actions as kiosksActions, Kiosk } from 'services/kiosk';
import { actions as salesErrorActions, selectors as salesErrorSelectors } from 'services/salesErrors';
import { formatText } from 'utils/stringFormat';
import { routes } from '../routesArray';
import { TimerOverlay } from 'components/TimerOverlay';
import { MaintenanceButton } from 'components/MaintenanceButton';
import { ActionOverlay } from 'components/ActionOverlay';
import { CartExpiration } from 'components/CartExpiration';
import { Typography, Box } from '@kis-technologies/kis-ui';
import get from 'lodash.get';
import { USER_ERROR } from 'constants/hardware';
import eventBus from './eventBus';

const disableTimerForPath = (path: string) =>
  routes
    .filter((route) => route.disableTimer)
    .map((route) => route.path)
    .some((routePath) => !!matchPath(routePath, path));
let timer: ReturnType<typeof setTimeout> | null = null;
let offlineTimer: ReturnType<typeof setTimeout> | null = null;

const TicketingRoutes = () => {
  // initial Load checks if this is the first time this is rendered
  const isInitialLoad = React.useRef(true);
  const initial = isInitialLoad && isInitialLoad.current;

  // Action Creators and Selectors
  const { releaseCart, resetTicketing, resetWillCall, checkSchedule, resetSalesError, checkLowStockThresholds } =
    useActions({
      releaseCart: cartActions.releaseCart,
      resetTicketing: ticketingActions.resetTicketing,
      resetWillCall: willCallActions.resetWillCall,
      checkSchedule: kiosksActions.checkSchedule,
      resetSalesError: salesErrorActions.resetError,
      checkLowStockThresholds: kiosksActions.checkLowStockThresholds
    });

  const location = useLocation();
  const navigate = useNavigate();
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const kioskLoading = useSelector(kioskSelectors.selectLoading);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const cart = useSelector(cartSelectors.selectCart);
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const cartError = useSelector(cartSelectors.selectError);
  const cartPurchased = useSelector(cartSelectors.selectPurchased);
  const purchaseError = useSelector(cartSelectors.selectPurchaseError);
  const salesError = useSelector(salesErrorSelectors.selectError);
  const salesErrorInfo = useSelector(salesErrorSelectors.selectAdditionalInfo);
  const salesErrorType = useSelector(salesErrorSelectors.selectErrorType);
  const paymentDevice = useSelector(hardwareSelectors.selectPaymentDevice);
  const ticketingLoading = useSelector(ticketingSelectors.selectLoading);

  const flowTemplate = get(config, 'configuration.flowTemplate');
  const inactivityTimeout = get(config, 'configuration.globalSettings.inactivityTimeout');
  const inactivityDuration = get(inactivityTimeout, 'duration');
  const navPromptEnabled = get(inactivityTimeout, 'navigationPrompt.enabled');
  const navPromptDuration = get(inactivityTimeout, 'navigationPrompt.duration');
  const abandonSessionEnabled = get(inactivityTimeout, 'abandonSession.enabled');
  const abandonSessionDuration = get(inactivityTimeout, 'abandonSession.duration');
  const palette = get(config, 'configuration.globalSettings.palette');

  const [timerOverlay, setTimerOverlay] = React.useState(false);
  const [navPromptDone, setNavPromptDone] = React.useState(true);

  // ***************
  // ** FUNCTIONS **
  // ***************

  const isOfflineScreen = () => {
    return location.pathname === '/ticketing/offline';
  };

  const backgroundStyle = () => {
    if (config && palette) {
      return { background: `${palette.darkPrimary}e5` };
    }
    return {};
  };

  // Hide Timer Overlay and redirect to attract loop
  const onResetClick = () => {
    setTimerOverlay(false);
    resetSalesError();
    resetTicketing();
    resetWillCall();
    checkLowStockThresholds();
    clearTimer();
    if (cart) releaseCart(cart.releaseParams);
    navigate('/ticketing/attract');
  };

  // Hide Timer Overlay and restart timer
  const onActiveClick = () => {
    setTimerOverlay(false);
    handleInactivityTimer();
  };

  const onMaintenanceButtonClick = () => {
    navigate('/maintenance/login');
  };

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const handleInactivityTimer = () => {
    clearTimer();
    if (!navPromptDone) return;
    if (
      location.pathname === '/ticketing/purchase/payment' &&
      !paymentDevice.loading &&
      paymentDevice.status.code === USER_ERROR
    ) {
      // Do Nothing here, skips the else case
    } else if (disableTimerForPath(location.pathname) || salesError) {
      return;
    }
    let timeout = (inactivityDuration as number) * 1000;
    if (navPromptEnabled) {
      timeout = (navPromptDuration as number) * 1000;
    }
    if (!timerOverlay) {
      // prevent a 2nd timer from starting
      timer = setTimeout(() => {
        if (abandonSessionEnabled && abandonSessionDuration) {
          setTimerOverlay(true);
        } else {
          if (inactivityTimeout) onResetClick();
        }
      }, timeout);
    }
  };

  // Handler for the 'navigationPrompt' event to reset inactivity timer
  React.useEffect(() => {
    eventBus.on('navigationPrompt', (data: any) => {
      if (data.detail.message) {
        setNavPromptDone(true);
      } else {
        setNavPromptDone(false);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Getting the kiosk schedule on initial application loading
    if (initial) {
      checkSchedule();
      isInitialLoad.current = false;
    }

    if (navPromptDone) {
      // Adding 'click' eventListener to reset the inactivityTimer
      document.addEventListener('click', handleInactivityTimer, true);
      document.addEventListener('keydown', handleInactivityTimer, true);
      handleInactivityTimer();
    }
    return () => {
      clearTimer();
      document.removeEventListener('click', handleInactivityTimer, true);
      document.removeEventListener('keydown', handleInactivityTimer, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, handleInactivityTimer, navPromptDone]);

  React.useEffect(() => {
    if (!paymentDevice.loading && paymentDevice.status.code === USER_ERROR) {
      handleInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentDevice.loading, paymentDevice.status.code]);

  // on mount gets the configuration object for the kiosk and sets initial load to false
  React.useEffect(() => {
    if ((salesError && salesErrorType) || purchaseError) {
      clearTimer();
      const offlineTimeout = (inactivityDuration as number) * 1000;
      offlineTimer = setTimeout(() => {
        // navigating to attract instead of offline, since the offline navigation is handled
        // at the render function based on the kiosk status
        navigate('/ticketing/attract');
        if (!purchaseError) {
          resetSalesError();
        }
      }, offlineTimeout);
    } else {
      if (timer && (cartReserving || ticketingLoading)) clearTimer();
      if (!ticketingLoading || cartError) handleInactivityTimer();
    }

    return () => {
      if (offlineTimer) clearTimeout(offlineTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReserving, ticketingLoading, cartError, salesError, salesErrorType, purchaseError]);

  React.useEffect(() => {
    if (navPromptDone) {
      handleInactivityTimer();
    } else {
      clearTimer();
      setTimerOverlay(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPromptDone]);

  // If the status is inactive, redirect to the offline page (OfflineView)
  if (kiosk.status === 'Inactive' && !isOfflineScreen() && !kioskLoading) {
    return <Navigate to="/ticketing/offline" />;
  }

  // If the status is active, redirect to the attract loop (AttractLoopView)
  if (kiosk.status === 'Active' && isOfflineScreen() && !kioskLoading) {
    return <Navigate to="/ticketing/attract" />;
  }

  // If setup not complete, redirect to the systems page (SystemCheckView)
  if (!kiosk.setupComplete) return <Navigate to="/systems" />;

  return (
    <>
      {/* Sales Error overlay */}
      {salesError && (
        <ActionOverlay
          flow={flowTemplate}
          style={backgroundStyle()}
          title={
            <Typography variant="h2" align="center">
              {formatText(salesError, flowTemplate)}
            </Typography>
          }
          height={660}
          description={
            <Box width={600}>
              <Typography align="center" fontSize={30}>
                {formatText(salesErrorInfo || '', flowTemplate)}
              </Typography>
            </Box>
          }
        />
      )}

      {/* TimerOverlay - displays when user inactive */}
      {abandonSessionEnabled && navPromptDone && timerOverlay && (
        <TimerOverlay
          flow={flowTemplate}
          onResetClick={onResetClick}
          onActiveClick={onActiveClick}
          startTime={abandonSessionDuration as number}
          style={{ zIndex: '2147483647' }}
        />
      )}

      {/* Tracks the cart expiration and displays an overlay when the time is reached. */}
      {cart && cart.expiration && !cartError && !cartPurchased && <CartExpiration />}

      {/* Hidden Maintenance Button */}
      <MaintenanceButton onClick={onMaintenanceButtonClick} style={{ zIndex: 2147483647 }} />
      <Routes>
        {/* Routes that are ticketing and protected */}
        {routes
          .filter((route) => route.ticketing && route.protected)
          .map((route) => (
            <Route path={route.path} key={route.path} element={<route.component />} />
          ))}
      </Routes>
    </>
  );
};

export default TicketingRoutes;
